import { PERMISSIONS } from "@/permissions/constant";
import { getUserPermissions, hasAnyPermission } from "@/permissions/rbac";

/**
 * Where a signed-in user belongs.
 *
 * One answer, used by three callers that used to disagree:
 *
 *   * `GuestGuard` — bouncing someone off /login who is already signed in
 *   * the login screen — where to go after a successful sign-in
 *   * `RouteGuard` — where to send someone who opened a route their account
 *     cannot reach
 *
 * The login screen used to send everyone to /admin/dashboard and let the
 * guards bounce them onward. A SuperAdmin saw the admin shell flash before
 * being redirected; a user whose role has no dashboard permission landed on
 * "Unauthorized" straight after a successful sign-in, which reads as a failed
 * login. Resolving the destination once, from what the account can actually
 * do, removes both.
 */

/**
 * Screens a signed-in user has no business seeing. GuestGuard redirects away
 * from these.
 *
 * Only the two that ask for credentials. The recovery flows are deliberately
 * left open: `/reset-password` and `/auth/accept-invite` are reached from an
 * emailed link and the token in that link is the credential, so they have to
 * work whether or not this browser happens to hold a session — bouncing
 * someone to the dashboard for following a link they were sent would strand
 * them. `/forgot-password` is the same story one step earlier.
 */
export const GUEST_ONLY_PATHS = ["/login", "/signup"];

/** Every auth screen. Wider than GUEST_ONLY_PATHS because none of them is a
 * sensible place to *return* someone to after they sign in, even the ones they
 * are allowed to open while signed in. */
export const AUTH_PATHS = [...GUEST_ONLY_PATHS, "/forgot-password", "/reset-password", "/auth/accept-invite"];

// First entry whose permissions the user holds wins. Ordered by how central the
// screen is to daily work, so the landing page is the one they would have
// clicked first anyway.
const CANDIDATE_LANDINGS = [
  { path: "/admin/dashboard", permissions: [PERMISSIONS.DASHBOARD_VIEW] },
  { path: "/admin/jobs", permissions: [PERMISSIONS.JOB_VIEW, PERMISSIONS.JOB_CREATE] },
  { path: "/admin/candidates", permissions: [PERMISSIONS.CANDIDATE_VIEW, PERMISSIONS.CANDIDATE_CREATE] },
  { path: "/admin/user", permissions: [PERMISSIONS.USER_VIEW] },
  { path: "/admin/role", permissions: [PERMISSIONS.ROLE_VIEW] },
];

export const SUPER_ADMIN_HOME = "/super-admin/dashboard";
// Last resort. Every signed-in account can open its own profile, so nobody is
// ever left without a destination.
export const FALLBACK_HOME = "/user-profile";

export const resolveLandingPath = (user) => {
  if (!user) return FALLBACK_HOME;
  if (user.isSuperAdmin) return SUPER_ADMIN_HOME;

  // isPending is a review marker, not an access restriction — an
  // AD-provisioned user holds a real default role from creation (see
  // get_or_create_user on the server) and lands wherever their permissions
  // send them, same as anyone else.
  const permissions = getUserPermissions(user);
  const match = CANDIDATE_LANDINGS.find((entry) => hasAnyPermission(entry.permissions, permissions));
  return match ? match.path : FALLBACK_HOME;
};

/**
 * Is `pathname` somewhere this user may be sent back to after signing in?
 *
 * Guards a deep link captured before the redirect to /login. Auth screens are
 * excluded — restoring the user to /login after they just signed in there
 * would loop — and so is the area belonging to the other kind of account.
 */
export const isRestorableTarget = (pathname, user) => {
  if (!pathname || typeof pathname !== "string" || !pathname.startsWith("/")) return false;
  if (AUTH_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`))) return false;
  if (user?.isSuperAdmin) return pathname.startsWith("/super-admin");
  return !pathname.startsWith("/super-admin");
};

export default resolveLandingPath;
